import { domainEvents } from '@/services/events/domainEvents'
import type { StreakRepository } from '@/repositories/StreakRepository'
import type { UserRepository } from '@/repositories/UserRepository'
import { STREAK_MILESTONES } from '@/domain/streak'
import type {
  EpisodeCompletedPayload,
  QuizPassedPayload,
  QuizFailedPayload,
  ScenarioCompletedPayload,
  StoryCompletedPayload,
  EscapeRoomCompletedPayload,
} from './events'

// P4 — records a day of meaningful learning activity toward the learner's
// streak whenever a real learning event fires. Deliberately does NOT listen
// to navigation or page-view events — opening the app doesn't count, only
// finishing something does (Document 11 §P4 streak requirement). Kept
// separate from GamificationService (XP/badges/certificates) since streaks
// are a distinct concern with their own repository and no XP side effects
// of their own — crossing a milestone emits 'streak.milestone' so
// GamificationService can award an achievement without StreakService and
// GamificationService racing to read/write the same streak record.
export class StreakService {
  constructor(private streaks: StreakRepository, private users: UserRepository) {
    domainEvents.on('episode.completed', (e) => this.record((e.payload as EpisodeCompletedPayload).userId))
    domainEvents.on('quiz.passed', (e) => this.record((e.payload as QuizPassedPayload).userId))
    domainEvents.on('quiz.failed', (e) => this.record((e.payload as QuizFailedPayload).userId))
    domainEvents.on('scenario.completed', (e) => this.record((e.payload as ScenarioCompletedPayload).userId))
    domainEvents.on('story.completed', (e) => this.record((e.payload as StoryCompletedPayload).userId))
    domainEvents.on('escaperoom.completed', (e) => this.record((e.payload as EscapeRoomCompletedPayload).userId))
  }

  private async record(userId: string) {
    const record = await this.streaks.recordActivity(userId)
    if (STREAK_MILESTONES.includes(record.currentStreak)) {
      const user = await this.users.getById(userId)
      await domainEvents.emit('streak.milestone', { userId, orgId: user?.orgId ?? null, streakDays: record.currentStreak })
    }
  }
}
